import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { ProductColor, ProductSize } from '../utils/productGrouping'

interface UseProductAttributesResult {
	colors: ProductColor[]
	sizes: ProductSize[]
	isLoading: boolean
	error: string | null
	refetch: () => Promise<void>
}

/**
 * Single source for loading a tenant's full color/size catalogs (the pattern
 * that was duplicated as `fetchColorsAndSizes` across ~8 forms). Fetches all
 * rows for the given admin, ordered the same way every screen expects
 * (colors by name, sizes by sort_order). Read-only: screens that also create/
 * delete colors (e.g. the inventory attribute manager) keep their own logic.
 */
export function useProductAttributes(adminId: string | null | undefined): UseProductAttributesResult {
	const [colors, setColors] = useState<ProductColor[]>([])
	const [sizes, setSizes] = useState<ProductSize[]>([])
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [error, setError] = useState<string | null>(null)

	const refetch = useCallback(async () => {
		if (!adminId) return
		setIsLoading(true)
		setError(null)
		const [colorsRes, sizesRes] = await Promise.all([
			supabase.from('product_colors').select('*').eq('admin_id', adminId).order('name'),
			supabase.from('product_sizes').select('*').eq('admin_id', adminId).order('sort_order'),
		])
		if (colorsRes.error || sizesRes.error) {
			setError(colorsRes.error?.message || sizesRes.error?.message || 'Failed to load product attributes')
			setIsLoading(false)
			return
		}
		setColors((colorsRes.data as ProductColor[]) || [])
		setSizes((sizesRes.data as ProductSize[]) || [])
		setIsLoading(false)
	}, [adminId])

	useEffect(() => {
		void refetch()
	}, [refetch])

	return { colors, sizes, isLoading, error, refetch }
}
